import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BookOpen } from 'lucide-react';
import { useHeroImage } from '@/hooks/useHeroImage';
import HeroImageEditor from '@/components/HeroImageEditor';
import EditableText from '@/components/EditableText';
import { ICONS } from '@/components/IconPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIERS = [
  { key: 'basic', label: 'BASIC PRACTICE', description: 'Foundational concepts and practice' },
  { key: 'comps_ready', label: "COMP'S READY", description: 'Comprehensive coverage • Board exam prep' },
  { key: 'advanced', label: 'ADVANCED', description: 'Advanced practice and mastery' }
];

export default function CoursesPage() {
  const [coursesByTierAndCategory, setCoursesByTierAndCategory] = useState({});
  const [testCategories, setTestCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const heroImage = useHeroImage('courses');
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load both courses and test categories
      const [coursesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/courses`),
        axios.get(`${API}/test-categories`)
      ]);
      
      // Set categories
      setTestCategories(categoriesRes.data.filter(cat => cat.enabled));
      
      // Group courses by tier and category
      const grouped = {};
      coursesRes.data
        .filter(course => course.status === 'published') // Only published courses
        .forEach(course => {
          // Determine tier (from course or category)
          const tier = course.tier || 'basic';
          const cat = course.category || 'general';
          
          if (!grouped[tier]) {
            grouped[tier] = {};
          }
          if (!grouped[tier][cat]) {
            grouped[tier][cat] = [];
          }
          grouped[tier][cat].push(course);
        });
      
      setCoursesByTierAndCategory(grouped);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCourseClick = (courseId) => {
    navigate(`/courses/${courseId}`);
  };

  return (
    <Layout>
      {/* Hero Section */}
      <div 
        className="relative bg-cover bg-center h-[400px] mb-12 flex items-center justify-center"
        style={heroImage ? { backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${heroImage})` } : { background: 'linear-gradient(135deg, #8B5A3C 0%, #6B4423 100%)' }}
      >
        <HeroImageEditor pageName="courses" />
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-7xl retro-title text-[#F4E4C1] mb-4" data-testid="page-title">
            <EditableText as="span" id="courses.hero.title">COURSES</EditableText>
          </h1>
          <p className="text-xl text-[#F4E4C1] max-w-3xl mx-auto">
            <EditableText as="span" id="courses.hero.subtitle">Professional Training • Traditional Chinese Medicine • Expert Instruction</EditableText>
          </p>
        </div>
      </div>

      {/* Content by Tier */}
      <div className="container mx-auto px-6 py-12 space-y-16">
        {loading ? (
          <div className="text-center py-20">
            <div className="text-2xl text-[#6B4423]">Loading courses...</div>
          </div>
        ) : (
          <>
            {TIERS.map((tier) => {
              // Get categories for this tier
              const tierCategories = testCategories.filter(cat => cat.tier === tier.key);
              const tierData = coursesByTierAndCategory[tier.key] || {};
              
              // Check if there are any courses for this tier
              const hasCourses = Object.keys(tierData).length > 0;
              if (!hasCourses) return null;
              
              return (
                <div key={tier.key} className="space-y-8">
                  {/* Tier Header */}
                  <div className="border-4 border-[#6B4423] bg-gradient-to-r from-[#F4E4C1] to-[#E8C49A] p-6 rounded-lg shadow-xl">
                    <h2 className="text-3xl retro-title text-[#2C1810] mb-2">
                      {tier.label}
                    </h2>
                    <p className="text-[#6B4423] text-lg">{tier.description}</p>
                  </div>

                  {/* Categories */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {tierCategories.map((category) => {
                      const coursesInCategory = tierData[category.slug] || [];
                      if (coursesInCategory.length === 0) return null;

                      return (
                        <div key={category.slug}>
                          {/* Category Header */}
                          <div className="cassette-card bg-gradient-to-br from-[#E8934D] to-[#6B4423] p-4 mb-4">
                            <h3 className="text-2xl retro-title text-white text-center">
                              {category.name}
                            </h3>
                          </div>

                          {/* Courses in this Category */}
                          <div className="space-y-4">
                            {coursesInCategory.map((course) => (
                              <button
                                key={course.id}
                                onClick={() => handleCourseClick(course.id)}
                                className="w-full cassette-card p-6 hover:scale-105 transition-all duration-200 cursor-pointer group"
                                data-testid={`course-card-${course.id}`}
                              >
                                <div className="flex items-center gap-4">
                                  {/* Course Icon/Image */}
                                  <div className="w-16 h-16 flex-shrink-0 rounded-lg bg-gradient-to-br from-[#E85D4D] via-[#E8934D] to-[#4D9E7C] flex items-center justify-center overflow-hidden">
                                    {course.cover_image_url ? (
                                      <img
                                        src={course.cover_image_url}
                                        alt={course.title}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : course.display_icon ? (
                                      (() => {
                                        const iconData = ICONS.find(i => i.name === course.display_icon);
                                        const IconComponent = iconData?.icon;
                                        return IconComponent ? (
                                          <IconComponent className="w-10 h-10 text-white" strokeWidth={1.5} />
                                        ) : (
                                          <BookOpen className="w-10 h-10 text-white" />
                                        );
                                      })()
                                    ) : (
                                      <BookOpen className="w-10 h-10 text-white" />
                                    )}
                                  </div>

                                  {/* Course Info */}
                                  <div className="flex-1 text-left">
                                    <h4 className="text-xl font-bold text-[#2C1810] group-hover:text-[#E85D4D] transition-colors mb-1">
                                      {course.title}
                                    </h4>
                                    <p className="text-sm text-[#6B4423]">
                                      {course.author}
                                    </p>
                                    {course.duration_hours && (
                                      <p className="text-xs text-[#8B6F47] mt-1">
                                        {course.duration_hours} hours
                                      </p>
                                    )}
                                  </div>

                                  {/* Arrow */}
                                  <div className="text-[#E85D4D] text-2xl group-hover:translate-x-2 transition-transform">
                                    →
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {Object.keys(coursesByTierAndCategory).length === 0 && (
              <div className="text-center py-20">
                <BookOpen className="w-24 h-24 mx-auto text-[#8B6F47] mb-6" />
                <h3 className="text-3xl retro-title text-[#2C1810] mb-4">
                  No Courses Available Yet
                </h3>
                <p className="text-xl text-[#6B4423]">
                  Check back soon for new course offerings!
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
